import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createCashIn,
  createCashOut,
  getCashIns,
  getCashOuts,
  getCashStatus,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import {
  DEFAULT_STALE_TIME,
  LOOKUP_STALE_TIME,
} from "../Constant/AppConstants";

export const cashQueryKeys = {
  cashIns: (params?: Parameters<typeof getCashIns>[0]) =>
    ["cashIns", params] as const,
  cashOuts: (params?: Parameters<typeof getCashOuts>[0]) =>
    ["cashOuts", params] as const,
  cashStatus: (params?: Parameters<typeof getCashStatus>[0]) =>
    ["cashStatus", params] as const,
};

export const invalidateCashQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["cashIns"] });
  queryClient.invalidateQueries({ queryKey: ["cashOuts"] });
  queryClient.invalidateQueries({ queryKey: ["cashStatus"] });
};

export const useCashInsQuery = (
  params: Parameters<typeof getCashIns>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: cashQueryKeys.cashIns(params),
    queryFn: async () => unwrapApiResponse(await getCashIns(params)),
  });
};

export const useCashInsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof getCashIns>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: cashQueryKeys.cashIns(params),
        queryFn: async () => unwrapApiResponse(await getCashIns(params)),
      }),
  });
};

export const useCashOutsQuery = (
  params: Parameters<typeof getCashOuts>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: cashQueryKeys.cashOuts(params),
    queryFn: async () => unwrapApiResponse(await getCashOuts(params)),
  });
};

export const useCashOutsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof getCashOuts>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: cashQueryKeys.cashOuts(params),
        queryFn: async () => unwrapApiResponse(await getCashOuts(params)),
      }),
  });
};

export const useCashStatusQuery = (
  params?: Parameters<typeof getCashStatus>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: cashQueryKeys.cashStatus(params),
    queryFn: async () => unwrapApiResponse(await getCashStatus(params)),
  });
};

export const useCashStatusRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: Parameters<typeof getCashStatus>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: cashQueryKeys.cashStatus(params),
        queryFn: async () => unwrapApiResponse(await getCashStatus(params)),
      }),
  });
};

export const usecreateCashInMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof createCashIn>[0]) =>
      createCashIn(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCashQueries(queryClient);
      }
    },
  });
};

export const usecreateCashOutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof createCashOut>[0]) =>
      createCashOut(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateCashQueries(queryClient);
      }
    },
  });
};

export const useCashService = (config?: {
  fetchCashInsParams?: Parameters<typeof getCashIns>[0];
  fetchCashInsEnabled?: boolean;
  fetchCashOutsParams?: Parameters<typeof getCashOuts>[0];
  fetchCashOutsEnabled?: boolean;
  fetchCashStatusParams?: Parameters<typeof getCashStatus>[0];
  fetchCashStatusEnabled?: boolean;
}) => {
  const cashInsQuery = useCashInsQuery(config?.fetchCashInsParams, {
    enabled:
      config?.fetchCashInsEnabled !== false &&
      config?.fetchCashInsParams !== undefined &&
      config?.fetchCashInsParams !== null,
  });

  const cashOutsQuery = useCashOutsQuery(config?.fetchCashOutsParams, {
    enabled:
      config?.fetchCashOutsEnabled !== false &&
      config?.fetchCashOutsParams !== undefined &&
      config?.fetchCashOutsParams !== null,
  });

  const cashStatusQuery = useCashStatusQuery(config?.fetchCashStatusParams, {
    enabled: config?.fetchCashStatusEnabled !== false,
  });

  const createCashInMutation = usecreateCashInMutation();
  const createCashOutMutation = usecreateCashOutMutation();

  return {
    cashInsQuery,
    cashOutsQuery,
    cashStatusQuery,
    createCashInMutation,
    createCashOutMutation,
  };
};
